import { useContext, useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { EditExpenseContext } from '../../../context/editContexts';
import classes from './Expense.module.scss';
import { useBlocker, useWindowSize, usePrevious } from '../../../utils/hooks';
import { appNotificationsTypes, responsiveSizeTimesheet, timesheetsStatuses } from '../../../utils/constants';
import NavigationComponent from '../../common/NavigationComponent/NavigationComponent';

import ExpenseHeader from './components/ExpenseHeader/ExpenseHeader';

import MainTable from './components/MainTable/MainTable';
import { tneAxiosClient } from '../../../api/apiSettings';
import { generalEnums } from '../../../utils/enums';
import BaseModal from '../../common/ModalWindows/BaseModal';
import WithCloseIcon from '../../common/ComposableWrappers/WithCloseIcon/WithCloseIcon';
import NotificationModal from '../../common/ModalWindows/NotificationModal';
import { getListOfApprovers } from '../../../actions/user';
import {
  addNewClaim,
  changeExpenseStatus,
  deleteClaimLineById,
  deleteExpenseRejectionComment,
  restoreDeletedExpenseLine,
  resetNewRow,
  saveExpenseComment,
  updateClaimTable,
  updateExpenseComment,
  updateClaim
} from '../../../actions/expense';
import { basicErrorHandler } from '../../../utils/helpers';
import {
  FinalModal,
  UnsavedChanges,
  DeletePopUp,
  PlacementModal,
  WithdrawModal,
  RejectModal,
  TimeandExpensePopup,
  SubmitModal
} from '../../common/ModalWindows/TNEModals/index';
import ApproverComment from '../timesheet/ApproverComment/ApproverComment';
import { PageContext } from '../../../context/context';

import TimeandExpenseActions from '../../common/buttons/TimeandExpenseActions/TimeandExpenseActions';
import MobileHeadMenu from '../timesheet/components/MobileHeadMenu/MobileHeadMenu';
import { deleteClaim } from '../../../actions/expenses';
import { removeNotification, setNotification } from '../../../actions/appNotifications';
import ExportModal from '../../common/ModalWindows/TNEModals/ExportModal/ExportModal';

export default function Expense() {
  const size = useWindowSize();
  const expense = useSelector((state) => state.expense.data);
  const approvers = useSelector((state) => state.user.approvers);
  const commentsaving = useSelector((state) => state.expense.commentPending);
  const titles = useSelector((state) => state.constFromApi.placements);
  const [isEdit, setIsEdit] = useState(size.width < responsiveSizeTimesheet);
  const isApprover = useSelector((state) => state.accessToParts.tAndEApprover);
  const [hasChanges, setHasChanges] = useState(false);
  const [openClaimSwiper, setOpenClaimSwiper] = useState(false);
  const [lineToDelete, setLineToDelete] = useState('');
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [saving, setSavaing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [openPopUp, setOpenPop] = useState('');
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ctx = useContext(PageContext);
  const [searchParams] = useSearchParams();
  const locationQueryParams = useLocation();
  const deletePending = useSelector((state) => state.expenses.pendingDelete);
  const prevPendingDelete = usePrevious(deletePending);

  useBlocker(() => setOpenPop('unsavedChanges'), hasChanges);

  const getExpenseAttachmentData = (data) => {
    const getExpenseData = data.ExpenseDetails.map((item) => ({
      rowId: item.Id,
      attachments: item.ExpenseAttachments,
      AddedNow: item?.AddedNow
    }));
    const getAttachmentData = getExpenseData.filter(
      (item) => item.attachments && !item.attachments.includes('Id') && typeof item.rowId === 'number' && item?.AddedNow
    );
    return getAttachmentData;
  };

  const getRequestFormData = (data) => {
    const formData = new FormData();
    const payCurrency = titles?.find((item) => item.PoNumber === expense?.PoNumber)?.PayCurrency;

    data?.ExpenseDetails?.forEach((detail, index) => {
      const detailCopy = {
        ...detail,
        Date: dayjs(detail['Date']).format(generalEnums.DATEFORMAT),
        FxCurrencyId: detail['FxCurrencyId'] ?? detail['ExpenseCurrencyId'],
        FxAmount: detail['FxAmount'] ?? 1
      };

      const fieldsToExclude = [
        'Category',
        'TotalAmount',
        'ExpenseAttachments',
        'attachments',
        'ExpenseCurrency',
        'FxCurrency',
        'AddedNow',
        ...(detail.Type === 'New' ? ['Id', 'Type'] : [])
      ];

      Object.keys(detailCopy).forEach((label) => {
        if (fieldsToExclude.includes(label)) return;
        if (detailCopy[label]?.length === 0) return;

        formData.append(`ExpenseDetails[${index}].${label}`, detailCopy[label]);
      });

      if (detailCopy['ExpenseCurrency'] === payCurrency) {
        formData.delete(`ExpenseDetails[${index}].FxAmount`);
        formData.delete(`ExpenseDetails[${index}].FxCurrency`);
      }

      detailCopy?.attachments?.forEach((attachment) => {
        formData.append(`ExpenseDetails[${index}].attachments`, attachment);
      });
    });

    return formData;
  };

  const saveIndividualCardMobile = () => {
    dispatch(updateClaimTable(expense));
    setHasChanges(true);
  };

  const exportHandler = () => {
    setOpenPop('export');
  };

  const saveHandler = async () => {
    if (
      [timesheetsStatuses(t).processed.apiStatus, timesheetsStatuses(t).approved.apiStatus].includes(
        expense.Status.Status
      )
    ) {
      return;
    }

    setSavaing(true);
    try {
      setHasChanges(false);
      const requestFormData = getRequestFormData(expense);
      const attachmentFormData = getExpenseAttachmentData(expense);
      attachmentFormData.map(async (file) => {
        const formData = new FormData();
        let getExpense = expense.ExpenseDetails.find((claim) => claim.Id === file.rowId);
        file.attachments.map((item) => {
          return formData.append('attachments', item);
        });
        const attachmentResponse = await tneAxiosClient.post(`v1/expense/attachment/${file.rowId}`, formData);
        getExpense = {
          ...getExpense,
          ExpenseAttachments: attachmentResponse.data.Data.Data
        };
        dispatch(updateClaim(getExpense));
      });
      const response = await tneAxiosClient.put(`v1/expense?expenseId=${expense.Id}`, requestFormData);
      setOpenClaimSwiper(false);
      if (response.status === 200) {
        const updatedExpenseDetails = response?.data?.Data?.ExpenseDetails.filter((item) => {
          if (item.ExpenseAttachments.length === 0) {
            item.NoReceiptCheck = true;
          }
          return item;
        });
        dispatch(
          updateClaimTable(response?.data?.Data, {
            ExpenseDetails: updatedExpenseDetails,
            isAllSameCurrency: updatedExpenseDetails.every(
              (detail) =>
                detail.ExpenseCurrency.CurrencyName === detail.FxCurrency.CurrencyName || detail.FxCurrency === null
            )
          })
        );
        dispatch(changeExpenseStatus(timesheetsStatuses(t).draft.apiStatus));
        dispatch(resetNewRow());
        setOpenPop('saved');
      }
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
    setSavaing(false);
  };

  const submitHandler = () => {
    if (
      expense.ExpenseDetails.some(
        (item) => item && item?.NoReceiptCheck === false && item.ExpenseAttachments?.length === 0
      )
    ) {
      setError(t('dashboard.expenses.validations.attachment'));
      return;
    }

    if (!expense.ExpenseDetails.every((item) => item.Comment)) {
      setError(t('dashboard.expenses.validations.comment'));
      return;
    }

    if (!expense.ExpenseDetails.every((item) => item?.ExpenseAmount !== 0)) {
      setError(t('dashboard.expenses.validations.expenseamount'));
      return;
    }

    setError('');
    dispatch(getListOfApprovers(expense.ClientCompanyId));

    setOpenPop('submit');
  };

  const totalClaim = useMemo(() => {
    return expense?.ExpenseDetails?.reduce((acc, obj) => {
      return parseFloat(acc) + parseFloat(obj.TotalAmount);
    }, 0);
  }, [expense?.ExpenseDetails]);

  const expenseCurrency = useMemo(() => {
    return expense?.ExpenseDetails[0]?.ExpenseCurrency?.Symbol;
  }, [expense?.ExpenseDetails]);

  const finalSubmitClaim = async (approver, cb) => {
    try {
      const formData = getRequestFormData(expense);
      formData.append('ExpenseStatus', timesheetsStatuses(t).submitted.apiStatus);
      formData.append('ApproverId', approver);
      await tneAxiosClient.put(`v1/expense?expenseId=${expense.Id}`, formData);
      setHasChanges(false);
      dispatch(changeExpenseStatus(timesheetsStatuses(t).submitted.apiStatus));
      setIsEdit(false);
      cb();
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
  };

  const finalApproveExpense = async () => {
    try {
      setApproving(true);
      await tneAxiosClient.post(`v1/expense-action-by-approver`, {
        ExpenseId: expense.Id,
        Status: timesheetsStatuses(t).approved.apiStatus
      });
      dispatch(changeExpenseStatus(timesheetsStatuses(t).approved.apiStatus));
      setOpenPop('approved');
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
    setApproving(false);
  };

  const finalRejectExpense = async () => {
    try {
      setRejecting(true);
      await tneAxiosClient.post(`v1/expense-action-by-approver`, {
        ExpenseId: expense.Id,
        Status: timesheetsStatuses(t).rejected.apiStatus
      });
      dispatch(changeExpenseStatus(timesheetsStatuses(t).rejected.apiStatus));
      setOpenPop('rejected');
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
    setRejecting(false);
  };

  const deleteLine = () => {
    if (expense.ExpenseDetails.length === 1) {
      dispatch(addNewClaim());
    }
    const deletedLine = expense.ExpenseDetails.find((item) => item.Id === lineToDelete);
    dispatch(deleteClaimLineById(lineToDelete));
    dispatch(
      setNotification({
        isAppStyled: true,
        message: `1 ${t('common.general.deleted')}`,
        duration: 3000,
        type: appNotificationsTypes.SUCCESS,
        action: (
          <p
            role="presentation"
            className={classes.undoBtn}
            onClick={() => {
              dispatch(restoreDeletedExpenseLine(deletedLine));
              dispatch(removeNotification());
            }}
          >
            {t('common.general.undo')}
          </p>
        )
      })
    );
    setHasChanges(true);
    setOpenPop('');
    setLineToDelete('');
  };

  const saveCommentHandler = (comment, commentId = '') => {
    if (commentId) {
      dispatch(updateExpenseComment(comment, commentId));
    } else {
      dispatch(saveExpenseComment(comment, expense.Id));
    }
  };

  const deleteCommentCB = (deletedComment) => {
    setOpenPop(false);
    dispatch(
      setNotification({
        isAppStyled: true,
        message: `1 ${t('common.general.deleted')}`,
        duration: 3000,
        type: appNotificationsTypes.SUCCESS,
        action: (
          <p
            role="presentation"
            className={classes.undoBtn}
            onClick={() => {
              saveCommentHandler(deletedComment.Reason);
              dispatch(removeNotification());
            }}
          >
            {t('common.general.undo')}
          </p>
        )
      })
    );
  };

  const deleteCommentHandler = (commentId) => {
    setCommentToDelete(expense.ExpenseRejectionReasons?.find((comment) => comment.Id === commentId));
    if (size.width > responsiveSizeTimesheet) {
      dispatch(
        deleteExpenseRejectionComment(commentId, () =>
          deleteCommentCB(expense.ExpenseRejectionReasons?.find((comment) => comment.Id === commentId))
        )
      );
    } else {
      setOpenPop('deleteComment');
    }
  };

  const finalDeleteComment = () => {
    dispatch(deleteExpenseRejectionComment(commentToDelete.Id, () => deleteCommentCB(commentToDelete)));
  };

  const deleteHandler = async () => {
    setOpenPop('deleteExpense');
  };

  const finalDeleteExpenseHandler = () => {
    dispatch(deleteClaim(expense?.Id));
  };

  const withdrawHandler = () => {
    setOpenPop('withdraw');
  };

  const finalWithdrawExpense = async () => {
    try {
      setWithdrawing(true);
      await tneAxiosClient.post(`v1/expense-action-by-approver`, {
        ExpenseId: expense.Id,
        Status: timesheetsStatuses(t).submitted.apiStatus,
        Reason: ''
      });
      dispatch(changeExpenseStatus(timesheetsStatuses(t).submitted.apiStatus));
      setOpenPop('');
      dispatch(
        setNotification({
          isAppStyled: true,
          message: t('dashboard.tnecommon.withdrawsuccessmsg'),
          duration: 3000,
          bottom: '147px',
          type: appNotificationsTypes.SUCCESS,
          action: (
            <p
              role="presentation"
              className={classes.undoBtn}
              onClick={() => {
                dispatch(removeNotification());
                finalApproveExpense();
              }}
            >
              {t('common.general.undo')}
            </p>
          )
        })
      );
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
    setWithdrawing(false);
  };

  const popUpModals = {
    export: (
      <ExportModal
        expense={expense}
        periodLabel={dayjs(expense?.ExpenseDetails?.[0]?.Date).format('MMMM YYYY')}
        onClose={() => setOpenPop('')}
      />
    ),
    saved: (
      <TimeandExpensePopup
        title={t('dashboard.expenses.table.saved')}
        primaryButton={{
          text: t('dashboard.expenses.newbuttontxt'),
          action: () => navigate('/dashboard/create-claim')
        }}
        secondaryButton={{
          text: t('common.general.gotodashboard'),
          action: () => navigate('/dashboard/expenses')
        }}
      />
    ),
    unsavedChanges: (
      <UnsavedChanges
        saveHandler={saveHandler}
        closeHandler={() => setOpenPop(false)}
        rejectPreventing={setHasChanges}
      />
    ),
    delete: (
      <DeletePopUp
        deleteHandler={() => deleteLine(lineToDelete)}
        closeHandler={() => {
          setOpenPop(false);
          setLineToDelete('');
        }}
      />
    ),
    deleteExpense: (
      <DeletePopUp
        deleteHandler={finalDeleteExpenseHandler}
        closeHandler={() => {
          setOpenPop(false);
        }}
        pending={deletePending}
      />
    ),
    deleteComment: (
      <DeletePopUp
        deleteHandler={finalDeleteComment}
        closeHandler={() => {
          setCommentToDelete(null);
          setOpenPop(false);
        }}
        pending={commentsaving}
      />
    ),
    submit: <SubmitModal submitHandler={finalSubmitClaim} approvers={approvers} Id={expense?.Id} />,
    approved: (
      <FinalModal
        message={
          ctx.pageType === generalEnums.TIMESHEET
            ? t('dashboard.timesheet.approvedsuccessfully')
            : t('dashboard.expenses.approvedsuccessfully')
        }
        isTimesheet={false}
        queryParams={locationQueryParams.search}
      />
    ),
    approve: (
      <TimeandExpensePopup
        title={t('dashboard.timesheet.approveconfirmation')}
        primaryButton={{
          text: t('common.general.approve'),
          action: finalApproveExpense
        }}
        pending={approving}
      />
    ),
    rejected: (
      <FinalModal
        message={
          ctx.pageType === generalEnums.TIMESHEET
            ? t('dashboard.timesheet.rejectedsuccessfully')
            : t('dashboard.expenses.rejectedsuccessfully')
        }
        isTimesheet={false}
        queryParams={locationQueryParams.search}
      />
    ),
    reject: <RejectModal />,
    withdraw: <WithdrawModal confirm={finalWithdrawExpense} pending={withdrawing} />,
    placement: <PlacementModal message={t('dashboard.expenses.placementStatus')} />
  };

  const rejectHandler = () => {
    if (expense?.ExpenseRejectionReasons.every((comment) => comment.ActionTaken !== 'Pending')) {
      setOpenPop('reject');
      return;
    }
    finalRejectExpense();
  };

  const checkIsRejectActive = () =>
    !expense?.ExpenseRejectionReasons.every((comment) => comment.ActionTaken !== 'Pending');

  const approveHandler = () => {
    setOpenPop('approve');
  };

  function alertUser(event) {
    if (!hasChanges) return undefined;
    event.preventDefault();
    event.returnValue = '';
    return '';
  }

  const placementHandler = () => {
    setOpenPop('placement');
  };

  useEffect(() => {
    if (prevPendingDelete && !deletePending) {
      navigate('/dashboard/expenses', { state: { idToDelete: expense.Id } });
    }
  }, [prevPendingDelete, deletePending]);

  useEffect(() => {
    const id = setInterval(() => {
      if (isEdit && hasChanges) {
        saveHandler();
      }
    }, 120000);
    return () => clearInterval(id);
  }, [isEdit, hasChanges, expense]);

  useEffect(() => {
    window.addEventListener('beforeunload', alertUser);
    return () => {
      window.removeEventListener('beforeunload', alertUser);
    };
  }, [hasChanges]);

  useEffect(() => {
    return () => {
      if (hasChanges) setOpenPop('unsavedChanges');
    };
  }, []);

  useEffect(() => {
    if (lineToDelete) setOpenPop('delete');
  }, [lineToDelete]);

  useEffect(() => {
    ctx.setPageType(generalEnums.EXPENSE);
  }, [ctx]);

  useEffect(() => {
    if (!isApprover && size.width < responsiveSizeTimesheet) setIsEdit(size.width < responsiveSizeTimesheet);
  }, [size.width]);

  useEffect(() => {
    const approverModal = searchParams.get('approver');
    const isOnlySubmitted = expense.Status.Status === timesheetsStatuses(t).submitted.apiStatus;
    if (approverModal === 'true' && isOnlySubmitted) {
      approveHandler();
    }
  }, [searchParams]);

  useEffect(() => {
    const editMode = searchParams.get('editMode');

    if (editMode === 'true' && expense?.Status?.Status === timesheetsStatuses(t).draft.apiStatus) {
      setIsEdit(true);
    }
  }, [searchParams]);

  const contextValue = useMemo(
    () => ({ isEdit, setIsEdit, expense, hasChanges, setHasChanges, openClaimSwiper, setOpenClaimSwiper }),
    [isEdit, setIsEdit, expense, hasChanges, setHasChanges, openClaimSwiper, setOpenClaimSwiper]
  );

  const closeHandler = () => {
    navigate(`/dashboard/expenses${locationQueryParams.search}`);
  };

  return (
    <EditExpenseContext.Provider value={contextValue}>
      <section className={classes.mainContainer}>
        {!(size.width > responsiveSizeTimesheet) && !hasChanges ? (
          <NavigationComponent label="" linkBack={`/dashboard/expenses${locationQueryParams.search}`}>
            <MobileHeadMenu
              isApprover={isApprover}
              data={expense}
              pageType={generalEnums.EXPENSE}
              deleteHandler={deleteHandler}
              isInfoIcon={[timesheetsStatuses(t).draft.apiStatus].includes(expense.Status.Status)}
              exportHandler={exportHandler}
            />
          </NavigationComponent>
        ) : (
          <div className={classes.closeContainer}>
            <button aria-label="Close" onClick={() => navigate(`/dashboard/expenses${locationQueryParams.search}`)}>
              <CloseIcon />
            </button>
          </div>
        )}
        <ExpenseHeader
          exportHandler={exportHandler}
          error={error}
          expense={expense}
          save={saveHandler}
          submit={submitHandler}
          approve={approveHandler}
          reject={rejectHandler}
          saving={saving}
          rejecting={rejecting}
          withdraw={withdrawHandler}
          isRejectActive={checkIsRejectActive()}
          isApprover={isApprover}
          getFormData={(data) => getRequestFormData(data)}
          placementStatus={expense.IsAssignmentCompletedForExpense}
          placementHandler={placementHandler}
          hasChanges={hasChanges}
        />
        <MainTable
          expenseCurrency={expenseCurrency}
          totalClaim={totalClaim}
          data={expense || []}
          save={saveHandler}
          submit={submitHandler}
          deleteLine={setLineToDelete}
          approve={approveHandler}
          reject={rejectHandler}
          saving={saving}
          isApprover={isApprover}
          getFormData={(data) => getRequestFormData(data)}
          placementStatus={expense.IsAssignmentCompletedForExpense}
          placementHandler={placementHandler}
          saveIndividualCardMobile={saveIndividualCardMobile}
        />
        {([
          timesheetsStatuses(t).rejected.apiStatus,
          timesheetsStatuses(t).draft.apiStatus,
          timesheetsStatuses(t).approved.apiStatus,
          timesheetsStatuses(t).processed.apiStatus,
          timesheetsStatuses(t).submitted.apiStatus
        ].includes(expense.Status.Status) ||
          (isApprover && expense.Status.Status === timesheetsStatuses(t).submitted.apiStatus)) && (
          <ApproverComment
            key={new Date()}
            comments={expense?.ExpenseRejectionReasons?.sort((a, b) => (a.Id > b.Id ? -1 : 1)) || []}
            disabled={
              !isApprover ||
              [
                timesheetsStatuses(t).approved.apiStatus,
                timesheetsStatuses(t).processed.apiStatus,
                timesheetsStatuses(t).rejected.apiStatus
              ].includes(expense.Status.Status)
            }
            isApprover={isApprover}
            extraClass={classes.commentSection}
            saveComment={saveCommentHandler}
            deleteComment={deleteCommentHandler}
            commentSaving={commentsaving}
          />
        )}
        {!(size.width > responsiveSizeTimesheet) && (
          <div className={classes.actions}>
            <TimeandExpenseActions
              error={error}
              saving={saving}
              rejecting={rejecting}
              close={closeHandler}
              save={saveHandler}
              submit={submitHandler}
              approve={approveHandler}
              reject={rejectHandler}
              withdraw={withdrawHandler}
              isRejectActive={checkIsRejectActive()}
              isApprover={isApprover}
              status={expense?.Status?.Status}
              placementStatus={expense.IsAssignmentCompletedForExpense}
              placementHandler={placementHandler}
            />
          </div>
        )}
        <BaseModal
          open={openPopUp}
          fullscreen={false}
          closeHandler={() => setOpenPop('')}
          title="&nbsp;"
          hideFooter
          hideHeader
          withSeparator={false}
          marginLeftRight={1}
          paperWidth={size.width > responsiveSizeTimesheet ? undefined : '100%'}
          paperStyles={
            size.width <= responsiveSizeTimesheet ? { margin: '15px', borderRadius: '10px' } : { borderRadius: '20px' }
          }
        >
          <WithCloseIcon close={() => setOpenPop('')}>{popUpModals[openPopUp]}</WithCloseIcon>
        </BaseModal>
        {error && (
          <NotificationModal open={error} onClose={() => setError('')}>
            <p className={classes.errorMessage}>{error}</p>
          </NotificationModal>
        )}
      </section>
    </EditExpenseContext.Provider>
  );
}
